import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";

import toast from "react-hot-toast";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormikSaveBar } from "../../../../components/utils/FormikSaveBar";
import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { FaqCategorySelector } from "../../../../components/utils/moduleSelectors/faqCategorySelector";
import { FaqLinkedResourcePicker } from "../../../../components/utils/moduleSelectors/faqLinkedResourcePicker";
import { QuillEditor } from "../../../../components/utils/quill-editor";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { dispatchWithToast } from "../../../../components/Utils";
import { useDispatch } from "react-redux";
import {
  createResourceHubFaqPostAsync,
  updateResourceHubFaqPostAsync,
} from "../../../../slices/resource-hub/faq/post";
import { fetchResourceHubFaqPost } from "../../../../api/resource-hub/faq/posts";

const FAQ_LINK_TYPES = [
  { value: "Hse::Document", labelKey: "resource_hub.faq.link_type.hse_document" },
  { value: "Vehicle", labelKey: "resource_hub.faq.link_type.vehicle" },
  { value: "Product", labelKey: "resource_hub.faq.link_type.product" },
  { value: "Report", labelKey: "resource_hub.faq.link_type.report" },
  { value: "Customer", labelKey: "resource_hub.faq.link_type.customer" },
  { value: "Project", labelKey: "resource_hub.faq.link_type.project" },
];

function mapPostToLinkedRecords(post) {
  const rows = post?.linked_records;
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows.map((r) => ({
    linkable_type: r.linkable_type || "",
    linkable_id: r.linkable_id != null ? String(r.linkable_id) : "",
    _title: r.title || "",
  }));
}

export const EditFaqPost = () => {
  const { t } = useTranslation();

  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);

  const [post, setPost] = useState(null);

  const getFaqPost = useCallback(
    async (postId) => {
      setIsLoading(true);
      try {
        const res = await fetchResourceHubFaqPost(postId);
        const body = res?.data;
        const record = body?.data !== undefined ? body.data : body;
        setPost(record);
      } catch {
        toast.error(
          t("Something went wrong when trying to load the report", {
            defaultValue: "Could not load FAQ post",
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (!id) {
      setPost(null);
      setIsLoading(false);
      return;
    }
    getFaqPost(id);
  }, [id, getFaqPost]);

  const dispatch = useDispatch();

  const createOrUpdateDispatch = (data) => {
    if (post?.id) {
      return dispatch(updateResourceHubFaqPostAsync({ ...data, id: post.id }));
    }
    return dispatch(createResourceHubFaqPostAsync(data));
  };

  const searchParams = new URLSearchParams(location.search);
  const userType = searchParams.get("userType") || "";

  const initialValues = useMemo(
    () => ({
      id: post?.id,
      title: post?.title || "",
      content: post?.content || "",
      category_id: post?.category?.id || null,
      public: post?.public || false,
      linked_records: mapPostToLinkedRecords(post),
      submit: null,
    }),
    [post]
  );

  function buildApiPayload(values) {
    const links = (values.linked_records || [])
      .filter(
        (r) =>
          r.linkable_type &&
          r.linkable_id !== "" &&
          r.linkable_id != null &&
          !Number.isNaN(Number(r.linkable_id))
      )
      .map((r) => ({
        linkable_type: r.linkable_type,
        linkable_id: Number(r.linkable_id),
      }));

    const payload = {
      title: values.title,
      content: values.content,
      category_id: values.category_id,
      public: values.public,
      linked_records: links,
    };

    if (post?.id) {
      payload.id = post.id;
    }

    return payload;
  }

  function Content() {
    return (
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={Yup.object({
                title: Yup.string().max(255).required(t("Title is Required")),
                category_id: Yup.number().required(t("Category is required")),
              })}
              onSubmit={(values, helpers) => {
                try {
                  const payload = buildApiPayload(values);
                  dispatchWithToast(createOrUpdateDispatch(payload), (saved) => {
                    const record =
                      saved?.data !== undefined && saved?.data !== null
                        ? saved.data
                        : saved;
                    if (record && typeof record === "object" && record.id) {
                      setPost(record);
                    }
                  });
                  helpers.setStatus({ success: true });
                  helpers.setSubmitting(false);
                } catch (err) {
                  toast.error("Nope");
                  console.error(err);
                  helpers.setStatus({ success: false });
                  helpers.setErrors({ submit: err.message });
                  helpers.setSubmitting(false);
                }
              }}
            >
              {(formik) => (
                <Form>
                  <div className="shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid sm:grid-cols-8 gap-6">
                        <div className="sm:col-span-6">
                          {userType !== "employee" && (
                            <PMSwitch
                              label="Public"
                              name="public"
                              {...formik}
                            />
                          )}
                        </div>
                        <div className="sm:col-span-2">
                          <FaqCategorySelector
                            name="category_id"
                            selected={post?.category}
                            userType={userType ? userType : null}
                            {...formik}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid sm:grid-cols-8 gap-6">
                        <div className="sm:col-span-8">
                          <PMField
                            name="title"
                            type="text"
                            label={t("Title")}
                            {...formik}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-4 bg-white py-6 px-4 sm:p-6">
                      <div>
                        <h3 className="text-base font-medium text-gray-900">
                          {t("resource_hub.faq.linked_records_title")}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {t("resource_hub.faq.linked_records_help")}
                        </p>
                      </div>

                      <FieldArray name="linked_records">
                        {({ push, remove }) => (
                          <div className="space-y-3">
                            {formik.values.linked_records?.map((row, index) => (
                              <div
                                key={index}
                                className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-start"
                              >
                                <div className="min-w-0 w-full sm:max-w-xs">
                                  <label
                                    className="block text-sm font-medium text-gray-700"
                                    htmlFor={`faq-link-type-${index}`}
                                  >
                                    {t("resource_hub.faq.linked_records_type")}
                                  </label>
                                  <select
                                    id={`faq-link-type-${index}`}
                                    name={`linked_records.${index}.linkable_type`}
                                    className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-8 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    value={row.linkable_type || ""}
                                    onChange={(e) => {
                                      formik.handleChange(e);
                                      formik.setFieldValue(
                                        `linked_records.${index}.linkable_id`,
                                        ""
                                      );
                                      formik.setFieldValue(
                                        `linked_records.${index}._title`,
                                        ""
                                      );
                                    }}
                                    onBlur={formik.handleBlur}
                                  >
                                    <option value="">
                                      {t("resource_hub.faq.linked_records_placeholder_type")}
                                    </option>
                                    {FAQ_LINK_TYPES.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {t(opt.labelKey)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="min-w-0 flex-1">
                                  {row.linkable_type ? (
                                    <FaqLinkedResourcePicker
                                      index={index}
                                      row={row}
                                      formik={formik}
                                    />
                                  ) : null}
                                </div>
                                <button
                                  type="button"
                                  className="inline-flex shrink-0 items-center justify-center rounded-md p-2 text-red-600 hover:bg-red-50"
                                  onClick={() => remove(index)}
                                  aria-label={t("Remove", { defaultValue: "Remove" })}
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-gray-50"
                              onClick={() =>
                                push({
                                  linkable_type: "",
                                  linkable_id: "",
                                  _title: "",
                                })
                              }
                            >
                              <PlusIcon className="h-5 w-5" />
                              {t("resource_hub.faq.linked_records_add")}
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  </div>

                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6">
                          <QuillEditor name="content" rows={20} {...formik} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <FormikSaveBar {...formik} />
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={[]}
        title={
          (!post?.id ? "New" : "Editing") +
          " FAQ Post" +
          (userType ? " for " : "") +
          userType.toUpperCase()
        }
      >
        {isLoading ? <ResourceLoading /> : <Content />}
      </DashboardHeader>
    </>
  );
};
